<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useTableFilters } from "../../composables/Table/useTableFilters";
import VDropdown from "../Dropdown/VDropdown.vue";
import VIcon from "../Icon/VIcon.vue";
import VTableFilterDropdown from "./VTableFilterDropdown.vue";

interface Props {
  field: string
}

const props = defineProps<Props>();

const { t } = useI18n();

const { getFilterForField } = useTableFilters();
const dropdown = ref<InstanceType<typeof VDropdown> | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const hasActiveFilter = computed(() => {
  return !!getFilterForField(props.field);
});

const filterLabel = computed(() => t("base.filter.tooltip"));

onClickOutside(wrapperRef, () => {
  if (isOpen.value) {
    dropdown.value?.hide();
  }
}, { ignore: [".v-popper__popper"] });
</script>

<template>
  <div ref="wrapperRef" class="inline-flex">
    <VDropdown
      ref="dropdown"
      theme="verbleif-popper"
      placement="bottom-start"
      :distance="8"
      :auto-hide="false"
      @show="isOpen = true"
      @hide="isOpen = false"
    >
      <button
        type="button"
        class="group ml-1 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 transition-all duration-200 hover:bg-(--gray-100) dark:hover:bg-dark-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
        :aria-label="filterLabel"
        :aria-pressed="hasActiveFilter"
        @click.stop
      >
        <VIcon
          :name="(hasActiveFilter ? 'funnel-x' : 'funnel')"
          class="text-xs transition-colors duration-200"
          aria-hidden="true"
          :class="
            hasActiveFilter
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-(--input-border) group-hover:text-primary-500 dark:group-hover:text-primary-400'
          "
        />
      </button>

      <template #popper="{ hide }">
        <VTableFilterDropdown
          :field="field"
          @close="hide"
        />
      </template>
    </VDropdown>
  </div>
</template>
